import React from 'react'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { AiOutlineInfoCircle } from 'react-icons/ai'
import { useNavigate } from 'react-router-dom'

import { prepaidVouchersApi } from '../../api/services/dd_panel_prepaid_vouchers'
import {
  PrepaidVoucher,
  PrepaidVouchersResponse
} from '../../api/services/dd_panel_prepaid_vouchers/types.ts'
import { useAuth } from '../../context/auth'
import {
  setPrepaidVouchersTableActions,
  setPrepaidVouchersTableColumns
} from '../../utils/prepaidVouchersTableSettings.tsx'
import Alert from '../common/Alert/Alert.tsx'
import Card from '../common/Card/Card.tsx'
import Loader from '../common/Loader/Loader.tsx'
import { Pagination } from '../common/Pagination'
import { usePaginatedQuery } from '../common/Pagination/usePaginatedQuery.ts'
import Table, { Action } from '../common/Table/Table.tsx'
import VoucherForm from './VoucherForm.tsx'

const fetchPrepaidVouchers = async ({
  page,
  filters
}: {
  page: number
  filters?: string
}): Promise<PrepaidVouchersResponse | null> => {
  const filterParams = `?page=${page}${filters ? `${filters}` : ''}`
  const res =
    await prepaidVouchersApi.getList<PrepaidVouchersResponse>(filterParams)

  return res.data
}

const PrepaidVouchers = ({
  patient_profile,
  showCounter = false
}: {
  patient_profile?: number
  showCounter?: boolean
}) => {
  const { state } = useAuth()
  const [filterParams, setFilterParams] = useState<string | undefined>(
    undefined
  )
  const [editVoucher, setEditVoucher] = useState<PrepaidVoucher | null>(null)
  const [formActivated, setFormActivated] = useState(false)

  const navigate = useNavigate()
  const title = 'Bezpłatne diety'

  useLayoutEffect(() => {
    if (patient_profile) setFilterParams(`&patient_profile=${patient_profile}`)
  }, [])

  const { data, isLoading, isError, currentPage, goToPage, totalPages } =
    usePaginatedQuery<PrepaidVouchersResponse>({
      queryKey: [
        'PrepaidVouchers',
        state.isAuthenticated,
        state.email,
        filterParams
      ],
      fetcher: ({ page, filterParams }) =>
        fetchPrepaidVouchers({ page, filters: filterParams }),
      pageSize: 10,
      filterParams
    })

  if (isError) {
    return <div>Wystąpił błąd podczas zapytania na serwer</div>
  }

  useEffect(() => {
    goToPage(1)
  }, [filterParams])

  const columns = useMemo(() => setPrepaidVouchersTableColumns(), [])
  const actions: Action<PrepaidVoucher>[] | undefined = useMemo(
    () =>
      setPrepaidVouchersTableActions(
        navigate,
        formActivated,
        editVoucher,
        setEditVoucher
      ),
    [formActivated, editVoucher, navigate]
  )

  return (
    <React.Fragment>
      <Card.Title>
        <div className="h-10 flex flex-row justify-between items-center">
          <span className="mr-3">{title}</span>
          <div>
            {!formActivated && <VoucherForm
              patient_profile={patient_profile}
              editVoucher={editVoucher}
              setEditVoucher={setEditVoucher}
              formActivated={formActivated}
              setFormActivated={setFormActivated}
            />}
          </div>
        </div>
      </Card.Title>

      <Card.Body>
        {isLoading && <Loader label={'Pobieranie danych...'} />}

        {data && data.results.length > 0 && !isLoading && (
          <>
            <Table data={data.results} columns={columns} actions={actions} />

            {showCounter && (
              <div className="mt-4 text-right">{`Liczba voucherów: ${data.count || 0}`}</div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={goToPage}
            />
          </>
        )}

        {!isLoading && data && data.results.length === 0 && (
          <div>
            <Alert
              type="info"
              message="Brak danych"
              icon={<AiOutlineInfoCircle className="text-lg" />}
            />
          </div>
        )}

        {formActivated && <VoucherForm
          patient_profile={patient_profile}
          editVoucher={editVoucher}
          setEditVoucher={setEditVoucher}
          formActivated={formActivated}
          setFormActivated={setFormActivated}
        />}
      </Card.Body>
    </React.Fragment>
  )
}

export default PrepaidVouchers
